import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  DecoratedProfileProperty,
  UndecoratedProfileProperty,
} from '@flemingmetrics/api-client';
import { mergeWithoutDuplicates } from '@/utils/arrays';
import { buildCompositeKey } from '@/store/utils';

type ProfilePropertiesState = {
  byId: Record<string, UndecoratedProfileProperty>;
  byProfileId: Record<string, string[]>;
};

const initialState: ProfilePropertiesState = {
  byId: {},
  byProfileId: {},
};

export const profilePropertiesSlice = createSlice({
  name: 'profileProperties',
  initialState,
  reducers: {
    addProfileProperty: (
      state,
      action: PayloadAction<DecoratedProfileProperty>
    ) => {
      return getNextState(state, [action.payload]);
    },
    setProfileProperties: (
      state,
      action: PayloadAction<DecoratedProfileProperty[]>
    ) => {
      return getNextState(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addDefaultCase(() => {});
  },
});

function formatProfileProperty({
  property,
  ...profileProperty
}: DecoratedProfileProperty) {
  return profileProperty;
}

function getNextState(
  state: ProfilePropertiesState,
  profileProperties: DecoratedProfileProperty[]
) {
  const byId = { ...state.byId };
  const byProfileId = { ...state.byProfileId };
  profileProperties.forEach((pp) => {
    const key = buildProfilePropertyKey(pp.profileId, pp.propertyId);
    byId[key] = pp;
    if (!byProfileId[pp.profileId]) {
      byProfileId[pp.profileId] = [];
    }
    byProfileId[pp.profileId] = mergeWithoutDuplicates(
      byProfileId[pp.profileId],
      [key]
    );
  });
  return { byId, byProfileId };
}

export function buildProfilePropertyKey(profileId: number, propertyId: number) {
  return buildCompositeKey(profileId, propertyId);
}

export const { addProfileProperty, setProfileProperties } =
  profilePropertiesSlice.actions;

export default profilePropertiesSlice.reducer;
